import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import { useMyMeetingAnnouncements } from '@/api/hooks/useMeetingAnnouncements';
import { useMyProfile } from '@/api/hooks/useProfile';
import apiClient from '@/api/client';
import {
  MeetingAnnouncementApplicationView,
  ApiPlatformCollectionResponse,
  extractCollectionItems,
} from '@/api/types';
import { playNotificationSound } from '@/utils/notificationSound';
import { chatService } from '@/services/firebase';

const STORAGE_KEY = '@tinomobile:seen_applications';

interface SeenApplicationsData {
  [applicationId: string]: {
    announcementId: string;
    seenAt: string;
  };
}

interface ApplicationNotificationsContextType {
  isLoading: boolean;
  totalNewCount: number;
  getNewCountForAnnouncement: (announcementId: string) => number;
  isApplicationNew: (applicationId: string) => boolean;
  markApplicationAsSeen: (applicationId: string, announcementId: string) => void;
  markAllForAnnouncementAsSeen: (announcementId: string) => void;
}

const ApplicationNotificationsContext = createContext<ApplicationNotificationsContextType | undefined>(undefined);

export const ApplicationNotificationsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();
  const { data: myProfile } = useMyProfile();
  const queryClient = useQueryClient();
  const [seenApplications, setSeenApplications] = useState<SeenApplicationsData>({});
  const [isStorageLoading, setIsStorageLoading] = useState(true);
  
  // Refs for sound notification logic
  const prevCountRef = useRef<number>(0);
  const isInitialLoadRef = useRef(true);
  
  const userId = myProfile?.id;

  // Fetch user's announcements
  const { data: announcements = [], isLoading: isAnnouncementsLoading } = useMyMeetingAnnouncements({
    enabled: isAuthenticated,
    refetchInterval: 30000, // Refetch announcements every 30 seconds
    refetchOnWindowFocus: true,
  });

  // Fetch applications for each announcement
  const applicationQueries = useQueries({
    queries: announcements.map((announcement) => ({
      queryKey: ['applicationNotifications', announcement.id] as const,
      queryFn: async () => {
        const response = await apiClient.get<ApiPlatformCollectionResponse<MeetingAnnouncementApplicationView>>(
          `/meeting-announcements/${announcement.id}/applications`
        );
        return {
          announcementId: announcement.id,
          applications: extractCollectionItems(response.data),
        };
      },
      enabled: isAuthenticated && announcements.length > 0,
      staleTime: 10000, // Cache for 10 seconds
      refetchInterval: 15000, // Refetch every 15 seconds for near real-time updates
      refetchIntervalInBackground: false, // Don't refetch when app is in background
      refetchOnWindowFocus: true, // Refetch when user returns to app
    })),
  });

  // Load seen applications from storage
  useEffect(() => {
    const loadSeenApplications = async () => {
      try {
        const stored = await AsyncStorage.getItem(STORAGE_KEY);
        if (stored) {
          setSeenApplications(JSON.parse(stored));
        }
      } catch (error) {
        console.error('Error loading seen applications:', error);
      } finally {
        setIsStorageLoading(false);
      }
    };

    if (isAuthenticated) {
      loadSeenApplications();
    } else {
      setSeenApplications({});
      setIsStorageLoading(false);
    }
  }, [isAuthenticated]);

  // Save seen applications to storage
  const saveSeenApplications = useCallback(async (data: SeenApplicationsData) => {
    try {
      await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving seen applications:', error);
    }
  }, []);

  // Get all applications from queries
  const allApplications = useMemo(() => {
    const apps: { applicationId: string; announcementId: string }[] = [];
    applicationQueries.forEach((query) => {
      if (query.data) {
        query.data.applications.forEach((app) => {
          apps.push({
            applicationId: app.id,
            announcementId: query.data!.announcementId,
          });
        });
      }
    });
    return apps;
  }, [applicationQueries]);

  // Calculate new applications (not seen)
  const newApplications = useMemo(() => {
    return allApplications.filter((app) => !seenApplications[app.applicationId]);
  }, [allApplications, seenApplications]);

  // Total new count
  const totalNewCount = newApplications.length;

  // Get new count for specific announcement
  const getNewCountForAnnouncement = useCallback((announcementId: string) => {
    return newApplications.filter((app) => app.announcementId === announcementId).length;
  }, [newApplications]);

  // Check if specific application is new
  const isApplicationNew = useCallback((applicationId: string) => {
    return !seenApplications[applicationId];
  }, [seenApplications]);

  // Mark single application as seen
  const markApplicationAsSeen = useCallback((applicationId: string, announcementId: string) => {
    setSeenApplications((prev) => {
      const updated = {
        ...prev,
        [applicationId]: {
          announcementId,
          seenAt: new Date().toISOString(),
        },
      };
      saveSeenApplications(updated);
      return updated;
    });
  }, [saveSeenApplications]);

  // Mark all applications for announcement as seen
  const markAllForAnnouncementAsSeen = useCallback((announcementId: string) => {
    const appsForAnnouncement = allApplications.filter(
      (app) => app.announcementId === announcementId
    );
    
    if (appsForAnnouncement.length === 0) return;

    setSeenApplications((prev) => {
      const updated = { ...prev };
      appsForAnnouncement.forEach((app) => {
        if (!updated[app.applicationId]) {
          updated[app.applicationId] = {
            announcementId,
            seenAt: new Date().toISOString(),
          };
        }
      });
      saveSeenApplications(updated);
      return updated;
    });
  }, [allApplications, saveSeenApplications]);

  // Determine loading state
  const isLoading = isStorageLoading || isAnnouncementsLoading || applicationQueries.some((q) => q.isLoading);

  // Play notification sound when new applications arrive
  useEffect(() => {
    // Skip during initial load
    if (isLoading && isInitialLoadRef.current) {
      return;
    }

    // After first load, mark as initialized and record initial count
    if (isInitialLoadRef.current && !isLoading) {
      isInitialLoadRef.current = false;
      prevCountRef.current = totalNewCount;
      console.log('ApplicationNotifications - initialized with count:', totalNewCount);
      return;
    }

    // Play sound when count increases
    if (totalNewCount > prevCountRef.current) {
      console.log('ApplicationNotifications - playing sound, count changed from', prevCountRef.current, 'to', totalNewCount);
      playNotificationSound();
    }
    prevCountRef.current = totalNewCount;
  }, [totalNewCount, isLoading]);

  // Reset on logout
  useEffect(() => {
    if (!isAuthenticated) {
      isInitialLoadRef.current = true;
      prevCountRef.current = 0;
    }
  }, [isAuthenticated]);

  // Subscribe to Firebase notifications for real-time updates
  useEffect(() => {
    if (!userId || !isAuthenticated) return;

    console.log('ApplicationNotifications - subscribing to Firebase notifications for user:', userId);
    
    const unsubscribe = chatService.subscribeToApplicationNotifications(
      userId,
      () => {
        // When Firebase notification arrives, refetch all application queries
        console.log('ApplicationNotifications - Firebase notification received, refetching...');
        queryClient.invalidateQueries({ queryKey: ['applicationNotifications'] });
      }
    );

    return () => {
      console.log('ApplicationNotifications - unsubscribing from Firebase notifications');
      unsubscribe();
    };
  }, [userId, isAuthenticated, queryClient]);

  const value: ApplicationNotificationsContextType = {
    isLoading,
    totalNewCount,
    getNewCountForAnnouncement,
    isApplicationNew,
    markApplicationAsSeen,
    markAllForAnnouncementAsSeen,
  };

  return (
    <ApplicationNotificationsContext.Provider value={value}>
      {children}
    </ApplicationNotificationsContext.Provider>
  );
};

export const useApplicationNotifications = (): ApplicationNotificationsContextType => {
  const context = useContext(ApplicationNotificationsContext);
  if (!context) {
    throw new Error('useApplicationNotifications must be used within ApplicationNotificationsProvider');
  }
  return context;
};
